import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signIn, signUp } from 'supertokens-auth-react/recipe/emailpassword';
import Passwordless, {
  createCode,
} from 'supertokens-auth-react/recipe/passwordless';
import Session from 'supertokens-auth-react/recipe/session';
import { getAuthorisationURLWithQueryParamsAndSetState } from 'supertokens-auth-react/recipe/thirdparty';
import { match } from 'ts-pattern';

import { useApiClient } from '@eluve/api-client-provider';
import { ApiConstants } from '@eluve/api-contract';
import {
  EmailPasswordAuthForm,
  type EmailPasswordAuthFormData,
} from '@eluve/blocks';
import {
  EluveNameLogo,
  HStack,
  Heading,
  Icon,
  NewButton,
  Text,
  VStack,
  useToast,
} from '@eluve/components';
import {
  ChallengerCodeForm,
  type ChallengerCodeFormData,
  EmailEntryForm,
  type EmailFormData,
  MagicLinkLoginControls,
  type RegistrationFormData,
  SocialLoginButtons,
} from '@eluve/feature-login';
import { useNamedLogger } from '@eluve/logger';
import { FeatureFlagKeysLookup } from '@eluve/posthog';
import { POSTHOG_ANONYMOUS_ID_KEY, usePostHog } from '@eluve/posthog-react';
import { generateStrongPassword } from '@eluve/utils';

import { appConfig } from '../config';
import {
  useLoginLoopDetection,
  useSessionPolling,
} from '../hooks/useAuthHooks';

type LoginState =
  | { type: 'email_entry' }
  | { type: 'challenger_code'; email: string }
  | { type: 'magic_link_login'; mode: 'register'; email: string }
  | { type: 'email_password'; mode: 'login' | 'register'; email: string };

type SocialProvider = 'google' | 'microsoft';

export const LoginPage: React.FC = () => {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const errorMessage = search.get('errorMessage') ?? '';
  const logger = useNamedLogger('LoginPage');
  const postHogClient = usePostHog();
  const toast = useToast();

  // Feature flags
  const isMagicLinkFFEnabled =
    useFeatureFlagEnabled(FeatureFlagKeysLookup.ENABLE_MAGIC_LINK_LOGIN) ??
    true;
  const isMicrosoftLoginFFEnabled =
    useFeatureFlagEnabled(FeatureFlagKeysLookup.ENABLE_MICROSOFT_LOGIN) ??
    false;

  const isMagicLinkEnabled =
    appConfig.VITE_ENABLE_MAGIC_LINK_LOGIN && isMagicLinkFFEnabled;
  const isMicrosoftLoginEnabled =
    appConfig.VITE_ENABLE_MICROSOFT_LOGIN && isMicrosoftLoginFFEnabled;

  const [loginState, setLoginState] = useState<LoginState>({
    type: 'email_entry',
  });
  const [isInitiatingLogin, setIsInitiatingLogin] = useState(false);
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [isRequestingNewCode, setIsRequestingNewCode] = useState(false);
  const [challengerError, setChallengerError] = useState<string | null>(null);
  const [isSubmittingRegistration, setIsSubmittingRegistration] =
    useState(false);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const [
    hasAgreedToTermsForEmailPassword,
    setHasAgreedToTermsForEmailPassword,
  ] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);

  useSessionPolling(isVerifyingEmail);
  const { redirectWithLogging, clearLoginLoopDetection } =
    useLoginLoopDetection();

  useEffect(() => {
    if (errorMessage) {
      logger.warn(errorMessage);
      toast.error(errorMessage);
    }
  }, [errorMessage, logger, toast]);

  useEffect(() => {
    postHogClient.capture('form_view', {
      page: loginState.type,
    });
  }, [loginState.type, postHogClient]);

  const handleInitiatePasswordlessFlow = async (
    email: string,
    isResendingCode = false,
  ) => {
    try {
      setIsRequestingNewCode(true);
      if (isResendingCode) {
        setChallengerError(null);
      }

      const response = await createCode({ email });
      if (response.status === 'OK') {
        logger.info('Challenger code sent successfully', { email });
        toast.success('Security code sent to your email!');

        if (!isResendingCode) {
          setIsVerifyingEmail(true);
          setLoginState({ type: 'challenger_code', email });
        } else {
          postHogClient.capture('code_resend', {
            email,
            status: 'success',
          });
        }
      } else {
        throw new Error('Code generation failed');
      }
    } catch (error) {
      logger.error('Failed to send challenger email', {
        error: error instanceof Error ? error.message : String(error),
        email,
      });
      toast.error('Failed to send code. Please try again.');
      if (isResendingCode) {
        postHogClient.capture('code_resend', {
          status: 'fail',
        });
      }
      throw error;
    } finally {
      setIsRequestingNewCode(false);
    }
  };

  const tryInitiateLogin = async (data: EmailFormData) => {
    const { email } = data;
    setIsInitiatingLogin(true);

    try {
      const currentPostHogId = postHogClient?.get_distinct_id();
      if (currentPostHogId) {
        localStorage.setItem(POSTHOG_ANONYMOUS_ID_KEY, currentPostHogId);
      }
      postHogClient?.setPersonProperties({ login_email: email });
    } catch (error) {
      logger.warn('Failed to set PostHog anonymous ID or person properties', {
        error,
      });
    }

    try {
      const response = await apiClient.auth.tryInitiateLogin({
        body: { email, appType: 'web' },
      });
      if (response.status !== 200) throw new Error('tryInitiateLogin failed');

      await match(response.body)
        .with({ type: 'saml' }, async ({ redirectUrl }) => {
          postHogClient.capture('user_login', {
            status: 'success',
            method: 'sso',
          });
          await redirectWithLogging(redirectUrl, { userEmail: email });
        })
        .with({ type: 'social' }, async ({ thirdPartyId }) => {
          try {
            const authUrl = await getAuthorisationURLWithQueryParamsAndSetState(
              {
                thirdPartyId,
                frontendRedirectURI: `${window.location.origin}/login/callback/${thirdPartyId}`,
              },
            );
            postHogClient.capture('user_login', {
              status: 'success',
              method: 'social',
            });
            await redirectWithLogging(authUrl, { userEmail: email });
          } catch {
            postHogClient.capture('user_login', {
              status: 'fail',
              method: 'social',
            });
            toast.error('Sign in failed');
          }
        })
        .with({ type: 'email' }, async ({ isExistingUser }) => {
          if (
            !isMagicLinkEnabled &&
            appConfig.VITE_ENABLE_EMAIL_PASSWORD_AUTH
          ) {
            postHogClient.capture('user_login', {
              method: 'email_password',
              mode: isExistingUser ? 'login' : 'register',
            });
            setLoginState({
              type: 'email_password',
              mode: isExistingUser ? 'login' : 'register',
              email,
            });
            return;
          }

          postHogClient.capture('user_login', {
            method: 'magic_link',
            mode: isExistingUser ? 'login' : 'register',
          });

          if (isExistingUser) {
            await handleInitiatePasswordlessFlow(email);
          } else {
            setLoginState({
              type: 'magic_link_login',
              mode: 'register',
              email,
            });
          }
        })
        .exhaustive();
    } catch {
      postHogClient.capture('user_login', {
        status: 'fail',
      });
      toast.error('Sign in failed');
    } finally {
      setIsInitiatingLogin(false);
    }
  };

  const handleChallengerCodeSubmit = async (data: ChallengerCodeFormData) => {
    setIsSubmittingCode(true);
    setChallengerError(null);

    try {
      logger.info('Submitting OTP code verification');

      const response = await Passwordless.consumeCode({
        userInputCode: data.challengerCode,
      });

      if (response.status === 'OK') {
        logger.info('OTP code verification successful');
        postHogClient.capture('code_verification', {
          status: 'success',
        });
        setIsVerifyingEmail(false);
        clearLoginLoopDetection();
        // Refresh tenant/session claims to pick up auto-approval if it occurred
        try {
          await apiClient.auth.refreshAllowedTenants({ body: {} });
        } catch {
          // non-fatal
        }
        const redirectTo = search.get('redirectTo') ?? '/';
        navigate(redirectTo);
      } else {
        logger.info('OTP verification failed', { response });
        postHogClient.capture('code_verification', {
          errorCode: response.status,
          status: 'fail',
        });
        if (response.status === 'INCORRECT_USER_INPUT_CODE_ERROR') {
          setChallengerError('Incorrect code. Please try again.');
        } else if (response.status === 'EXPIRED_USER_INPUT_CODE_ERROR') {
          setChallengerError(
            'Security code has expired. Please request a new one.',
          );
        } else {
          setChallengerError('Invalid security code. Please try again.');
        }
      }
    } catch (error) {
      logger.error('Error verifying OTP code', { error });
      postHogClient.capture('code_verification', {
        status: 'fail',
      });
      setChallengerError('Invalid security code. Please try again.');
    } finally {
      setIsSubmittingCode(false);
    }
  };

  const handleRegistrationSubmit = async (data: RegistrationFormData) => {
    setIsSubmittingRegistration(true);
    try {
      const inviteCode = search.get(ApiConstants.INVITE_CODE);

      // Create account with SuperTokens first
      const strongDummyPassword = generateStrongPassword();
      const response = await signUp({
        formFields: [
          { id: 'email', value: data.email },
          { id: 'password', value: strongDummyPassword },
        ],
        options: {
          preAPIHook: async (context) => {
            let body: Record<string, unknown> = {};
            const ogBody = context.requestInit.body;
            if (typeof ogBody === 'string') {
              body = JSON.parse(ogBody);
              body[ApiConstants.INVITE_CODE] = inviteCode;
              // Mark this as a magic link registration
              body.magicLinkRegistration = true;
            }

            return {
              url: context.url,
              requestInit: {
                ...context.requestInit,
                body: JSON.stringify(body),
              },
            };
          },
        },
      });

      if (response.status === 'OK') {
        logger.info(
          'Registration successful, initiating verification code flow',
          {
            email: data.email,
          },
        );
        // Set verification flag before signing out to prevent race condition
        setIsVerifyingEmail(true);
        // Ensure no active session so user must verify (ignore errors)
        try {
          const serverUserId = await Session.getUserId();

          logger.info('Server User ID', {
            userId: serverUserId,
            postHogId: postHogClient.get_distinct_id(),
          });

          if (serverUserId) {
            postHogClient.identify(serverUserId, { email: data.email });
          }

          await Session.signOut();
        } catch {
          // ignore signOut errors
        }
        await handleInitiatePasswordlessFlow(data.email);
      } else {
        logger.warn('Failed to register', {
          status: response.status,
          email: data.email,
          formFieldErrors:
            'formFields' in response ? response.formFields : undefined,
        });

        if (
          response.status === 'FIELD_ERROR' &&
          response.formFields.some(
            (f) => f.id === 'email' && f.error.includes('email already exists'),
          )
        ) {
          toast.error('Email already exists. Switching to login mode.');
        } else {
          toast.error('Registration failed. Please try again.');
        }
      }
    } catch (error) {
      logger.error('Registration error', { error, email: data.email });
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  const handleSocialLogin = async (provider: SocialProvider) => {
    try {
      const inviteCode = search.get(ApiConstants.INVITE_CODE);
      const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: provider,
        frontendRedirectURI: `${window.location.origin}/login/callback/${provider}`,
        userContext: {
          [ApiConstants.INVITE_CODE]: inviteCode,
        },
        options: {},
      });

      window.location.assign(authUrl);
    } catch {
      toast.error('Sign in failed');
    }
  };

  const renderMainContent = () =>
    match(loginState)
      .with({ type: 'challenger_code' }, ({ email: challengerEmail }) => (
        <ChallengerCodeForm
          email={challengerEmail}
          onSubmit={handleChallengerCodeSubmit}
          onRequestNewCode={(email) =>
            handleInitiatePasswordlessFlow(email, true)
          }
          isSubmitting={isSubmittingCode}
          isRequestingNewCode={isRequestingNewCode}
          error={challengerError}
        />
      ))
      .with({ type: 'email_password' }, ({ email: epEmail, mode }) => (
        <VStack align="center" gap={0}>
          <EmailPasswordAuthForm
            initialEmail={epEmail}
            initialMode={mode}
            // Do not show magic link button in password-first mode
            showMagicLinkButton={false}
            onSendEmail={undefined}
            showBaaAgreement={true}
            hasAgreedToBaa={hasAgreedToTermsForEmailPassword}
            onBaaAgreementChange={setHasAgreedToTermsForEmailPassword}
            onSubmit={async (
              data: EmailPasswordAuthFormData,
            ): Promise<'login' | 'register' | undefined> => {
              try {
                if (data.type === 'login') {
                  const response = await signIn({
                    formFields: [
                      { id: 'email', value: data.email },
                      { id: 'password', value: data.password },
                    ],
                  });

                  if (response.status === 'OK') {
                    const redirectTo =
                      new URLSearchParams(window.location.search).get(
                        'redirectTo',
                      ) ?? '/';
                    navigate(redirectTo);
                    return undefined;
                  }

                  if (response.status === 'WRONG_CREDENTIALS_ERROR') {
                    toast.error('Incorrect email or password');
                    return undefined;
                  }

                  toast.error('Login failed. Please try again.');
                  return undefined;
                } else {
                  // register
                  const searchParams = new URLSearchParams(
                    window.location.search,
                  );
                  const inviteCode = searchParams.get(ApiConstants.INVITE_CODE);

                  const response = await signUp({
                    formFields: [
                      { id: 'email', value: data.email },
                      { id: 'password', value: data.password },
                    ],
                    options: {
                      preAPIHook: async (context) => {
                        let body: Record<string, unknown> = {};
                        const ogBody = context.requestInit.body;
                        if (typeof ogBody === 'string') {
                          body = JSON.parse(ogBody);
                          if (inviteCode)
                            body[ApiConstants.INVITE_CODE] = inviteCode;
                        }
                        return {
                          url: context.url,
                          requestInit: {
                            ...context.requestInit,
                            body: JSON.stringify(body),
                          },
                        };
                      },
                    },
                  });

                  if (response.status === 'OK') {
                    const redirectTo =
                      new URLSearchParams(window.location.search).get(
                        'redirectTo',
                      ) ?? '/';
                    navigate(redirectTo);
                    return undefined;
                  }

                  if (
                    response.status === 'FIELD_ERROR' &&
                    'formFields' in response &&
                    response.formFields.some(
                      (f) =>
                        f.id === 'email' &&
                        f.error.includes('email already exists'),
                    )
                  ) {
                    setLoginState({
                      type: 'email_password',
                      mode: 'login',
                      email: data.email,
                    });
                    toast.error('Email already exists. Switching to login.');
                    return 'login';
                  }

                  toast.error('Registration failed. Please try again.');
                  return undefined;
                }
              } catch {
                toast.error('Something went wrong. Please try again.');
                return undefined;
              }
            }}
          />

          {/* Social Login Buttons */}
          {appConfig.VITE_ENABLE_SOCIAL_AUTH && (
            <SocialLoginButtons
              isMicrosoftLoginEnabled={isMicrosoftLoginEnabled}
              onGoogleLogin={() => handleSocialLogin('google')}
              onMicrosoftLogin={() => handleSocialLogin('microsoft')}
              disabled={
                mode === 'register' && !hasAgreedToTermsForEmailPassword
              }
            />
          )}
        </VStack>
      ))
      .with({ type: 'email_entry' }, () => (
        <EmailEntryForm
          onSubmit={tryInitiateLogin}
          isLoading={isInitiatingLogin}
        />
      ))
      .with({ type: 'magic_link_login' }, ({ email: stateEmail }) => (
        <VStack align="center" gap={0}>
          <MagicLinkLoginControls
            email={stateEmail}
            isBusy={isRequestingNewCode || isSubmittingRegistration}
            onRegistrationSubmit={handleRegistrationSubmit}
            onAgreeToTermsChange={(agreed) => setHasAgreedToTerms(agreed)}
          />
          {appConfig.VITE_ENABLE_SOCIAL_AUTH && (
            <SocialLoginButtons
              isMicrosoftLoginEnabled={isMicrosoftLoginEnabled}
              onGoogleLogin={() => handleSocialLogin('google')}
              onMicrosoftLogin={() => handleSocialLogin('microsoft')}
              disabled={!hasAgreedToTerms}
            />
          )}
        </VStack>
      ))
      .exhaustive();

  const renderHeaderText = () =>
    match(loginState)
      .with({ type: 'challenger_code' }, () => (
        <HStack justify="between" align="center">
          <Heading weight="bold" color="primary" size="3xl">
            Check your email
          </Heading>
          <span className="w-14" />
        </HStack>
      ))
      .with({ type: 'email_password' }, ({ mode }) => (
        <HStack justify="between" align="center">
          <Heading weight="bold" color="primary" size="2xl">
            {mode === 'login'
              ? 'Log into your account'
              : 'Create an account for free'}
          </Heading>
          <span className="w-14" />
        </HStack>
      ))
      .with({ type: 'magic_link_login' }, () => (
        <>
          <HStack justify="between" align="center">
            <Heading weight="bold" color="primary" size="2xl">
              Create an account for free
            </Heading>
            <span className="w-14" />
          </HStack>
          <HStack className="mt-2" gap={4}>
            <HStack gap={4} wFull={false}>
              <Icon name="Check" className="size-4 " />
              <Text size="lg" color="primary">
                30-day free trial
              </Text>
            </HStack>
            <HStack gap={4} wFull={false}>
              <Icon name="Check" className="size-4" />
              <Text size="lg" color="primary">
                No credit card required
              </Text>
            </HStack>
          </HStack>
        </>
      ))
      .otherwise(() => (
        <>
          <HStack justify="between" align="center">
            <Heading weight="bold" color="primary" size="3xl">
              Welcome to Eluve
            </Heading>
            <span className="w-14" />
          </HStack>
          <Heading weight="semibold" color="tertiary" size="2xl">
            Login or sign up
          </Heading>
        </>
      ));

  return (
    <>
      <Helmet>
        <title>Login | Eluve</title>
      </Helmet>
      <HStack>
        <VStack
          align="start"
          justify="center"
          className="h-screen w-screen p-8"
        >
          <EluveNameLogo height={60} className="absolute left-8 top-8" />
          <VStack
            align="center"
            justify="between"
            className="mx-auto h-full max-w-[460px]"
          >
            <VStack className="grow">&nbsp;</VStack>
            <VStack gap={4}>
              {match(loginState)
                .with({ type: 'email_entry' }, () => null)
                .otherwise(() => (
                  <NewButton
                    text="Back to login"
                    type="ghost"
                    size="s"
                    icon={{ name: 'ArrowLeft' }}
                    onClick={() => {
                      setLoginState({ type: 'email_entry' });
                      setIsVerifyingEmail(false);
                    }}
                  />
                ))}
              {renderHeaderText()}
            </VStack>
            <VStack>
              {/* Render the main content based on login state */}
              {renderMainContent()}
            </VStack>
            <VStack className="grow">&nbsp;</VStack>
            {/* Terms and Privacy links */}
            <HStack justify="center" gap={3} className="mt-auto px-4">
              <a
                className="text-sm text-text-tertiary underline hover:text-text-secondary"
                href="https://www.eluve.com/terms"
                target="_blank"
                rel="noreferrer"
              >
                Terms and Conditions
              </a>
              <a
                className="text-sm text-text-tertiary underline hover:text-text-secondary"
                href="https://www.eluve.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy policy
              </a>
            </HStack>
          </VStack>
        </VStack>

        <div className="hidden h-screen w-0 md:block md:w-9/12">
          <img
            src="/side_image.jpg"
            alt="Side panel a woman holding a laptop"
            className="h-full w-auto object-cover"
          />
        </div>
      </HStack>
    </>
  );
};
